import React, { useCallback, useState } from "react";
// import { useResizeObserver } from "@wojtekmaj/react-hooks";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

import type { PDFDocumentProxy } from "pdfjs-dist";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import { Button, Col, Row, Typography } from "antd";
import { Icon } from "@iconify/react";

// El `workerSrc` se configura una sola vez en `utils/configurePdfWorker.ts`
// (worker del bundle, build `legacy`). No lo sobreescribas aqui con una URL de
// CDN: ese era el origen de MB-APP-EXPRESS-X0.

const options = {
  cMapUrl: "/cmaps/",
  standardFontDataUrl: "/standard_fonts/",
};

const resizeObserverOptions = {};

type PreviewPdfProps = {
  pdfBlob: Blob;
  currentPage: number;
  setCurrentPage: (value: number) => void;
  password: string | null;
};

const { Text } = Typography;

const PreviewPdf: React.FC<PreviewPdfProps> = ({
  pdfBlob,
  currentPage,
  setCurrentPage,
  password,
}) => {
  const screen = useBreakpoint();
  const [numPages, setNumPages] = useState<number>();
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>();
  const [loadFailed, setLoadFailed] = useState(false);

  const onResize = useCallback<ResizeObserverCallback>((entries) => {
    const [entry] = entries;

    if (entry) {
      setContainerWidth(entry.contentRect.width);
    }
  }, []);

  const maxWidth = !screen.md ? 300 : 750;

  // useResizeObserver(containerRef, resizeObserverOptions, onResize);

  function onDocumentLoadSuccess({
    numPages: nextNumPages,
  }: PDFDocumentProxy): void {
    setLoadFailed(false);
    setNumPages(nextNumPages);
  }

  // Solo estado local: NO reportamos a Sentry desde aqui. Este camino tambien
  // se dispara por causas legitimas (PDF corrupto, contrasena incorrecta), asi
  // que capturarlo abriria un issue ruidoso nuevo en lugar de cerrar el viejo.
  const onDocumentLoadError = useCallback((): void => {
    setLoadFailed(true);
    setNumPages(undefined);
  }, []);

  const goToPreviousPage = (): void => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = (): void => {
    if (currentPage < numPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const downloadPdf = (): void => {
    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "documento.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Sin esto react-pdf muestra su default en ingles ("Failed to load PDF
  // file.") y, cuando el worker ni arranca, el usuario se queda en un
  // "Loading PDF..." eterno. La descarga directa no necesita el worker: el
  // blob ya esta en memoria.
  const errorFallback = (
    <div style={{ padding: 24, textAlign: "center" }}>
      <Text strong>No pudimos mostrar la vista previa del PDF.</Text>
      <div style={{ marginTop: 8 }}>
        <Text type="secondary">
          Puedes descargarlo y abrirlo con el lector de tu dispositivo.
        </Text>
      </div>
      <div style={{ marginTop: 16 }}>
        <Button type="primary" onClick={downloadPdf}>
          Descargar PDF
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      <div ref={setContainerRef}>
        <Document
          file={pdfBlob}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          error={errorFallback}
          options={options}
          onPassword={(callback) => callback(password)}
        >
          <Page
            key={`page_${currentPage}`}
            pageNumber={currentPage}
            width={
              containerWidth ? Math.min(containerWidth, maxWidth) : maxWidth
            }
          />
        </Document>
        {!loadFailed && (
          <div>
            <Row justify={"center"} align={"middle"}>
              <Col span={8} style={{ textAlign: "center" }}>
                <div style={{ cursor: "pointer" }} onClick={goToPreviousPage}>
                  <Icon icon="fe:arrow-left" fontSize={30} color={"#0f5ef7"} />
                </div>
              </Col>
              <Col span={8} style={{ textAlign: "center" }}>
                Página <Text strong>{currentPage}</Text> de{" "}
                {<Text strong>{numPages}</Text>}
              </Col>
              <Col span={8} style={{ textAlign: "center" }}>
                <div style={{ cursor: "pointer" }} onClick={goToNextPage}>
                  <Icon icon="fe:arrow-right" fontSize={30} color={"#0f5ef7"} />
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewPdf;
